/**
 * periodDates — 학생 v2 의 날짜·학년 계산. 서버(요청서 §3-2·§4)와 **같은 식**을 쓴다.
 *
 * 계약: docs/student-admin-v2/FE-20261009-student-admin-v2-backend-design.html
 *   - 기간은 [start_on, end_on) — 종료일 = 그 상태가 아닌 첫날, 종료일이 비면 진행 중(§3-2 2번)
 *   - 일수 = 종료일 − 시작일(§3-2 4번). 진행 중인 기간은 기준일까지 — 기준일 당일을 셈에 넣는다
 *     (요청서 SC7: 9/1 재등록, 기준일 10/9 → 39일)
 *   - 학년은 계산하지 않는다 — 학교·학년 기간 행의 학년(grade_at_start)이 그 기간 내내 그대로다.
 *     자동 올림 없음 · 올림일 설정 없음(오너 결정 2026-10-12, 백엔드 0단계 전달 §3-1).
 *     바꾸려면 [학교·학년 변경]으로 새 기간을 연다.
 *
 * 날짜는 'YYYY-MM-DD' 글자로 주고받고, 계산은 UTC 날 번호로 한다 — 브라우저 시간대가 끼어들지 않게.
 * 「오늘」만 한국 시간으로 정한다(§3-2 1번).
 */

const DAY_MS = 86400000;
const WEEKDAY_LABELS = ['월', '화', '수', '목', '금', '토', '일'];

/** 'YYYY-MM-DD' → 날 번호(1970-01-01 = 0). 형식이 아니면 null */
export function toDayNumber(date) {
  if (typeof date !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS;
}

/** 날 번호 → 'YYYY-MM-DD' */
export function fromDayNumber(n) {
  return new Date(n * DAY_MS).toISOString().slice(0, 10);
}

/** 한국 기준 오늘 'YYYY-MM-DD' */
export function todayKST(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export function addDays(date, n) {
  const d = toDayNumber(date);
  return d == null ? null : fromDayNumber(d + n);
}

/** end − start (날). 둘 중 하나라도 없으면 null */
export function daysBetween(start, end) {
  const a = toDayNumber(start);
  const b = toDayNumber(end);
  return a == null || b == null ? null : b - a;
}

/**
 * 기준일까지의 일수 — 닫힌 기간은 종료일 − 시작일, 진행 중이거나 종료일이 기준일보다 뒤면 기준일 당일까지.
 * 기준일보다 뒤에 시작하는 기간은 0.
 */
export function elapsedDays(start, end, asOf) {
  const a = toDayNumber(start);
  const d = toDayNumber(asOf);
  if (a == null || d == null || a > d) return 0;
  const e = toDayNumber(end);
  const stop = e == null ? d + 1 : Math.min(e, d + 1);
  return Math.max(0, stop - a);
}

/** 그 상태였던 마지막 날 = 종료일 하루 전(§3-2 8번 last_day). 「반에 있는 마지막 날」이 이것이다 */
export function lastDayOf(end) {
  return end ? addDays(end, -1) : null;
}

/** 퇴원 표시 날짜. 저장 경계는 그대로 두고, 옛 응답을 읽을 때만 하루 전을 구한다. */
export const tenureLastDay = (period) => period?.last_day ?? lastDayOf(period?.end_on);
/** 반 편성도 마지막 수강일을 표시한다. 일수·겹침 계산은 end_on 경계를 유지한다. */
export const enrollmentLastDay = (period) => period?.last_day ?? lastDayOf(period?.end_on);
export const withdrawalDay = (student) => student?.withdraw_last_day ?? lastDayOf(student?.withdraw_scheduled_on);

/** 날짜 d 에 기간이 걸렸는가 — start ≤ d < end(§3-2 3번) */
export function isActiveOn(period, date) {
  if (!period) return false;
  const d = toDayNumber(date);
  const a = toDayNumber(period.start_on);
  if (d == null || a == null || a > d) return false;
  const e = toDayNumber(period.end_on);
  return e == null || d < e;
}

/** ISO 요일 — 월 1 … 일 7 */
export function isoWeekday(date) {
  const d = toDayNumber(date);
  if (d == null) return null;
  return ((new Date(d * DAY_MS).getUTCDay() + 6) % 7) + 1;
}

export function weekdayLabel(isoDay) {
  return WEEKDAY_LABELS[isoDay - 1] || '';
}

/**
 * 시간표상 마지막 수업 — 기간 [start_on, end_on) 안에서 시간표 요일에 맞는 마지막 날.
 * 퇴반일만 받고 「반에 있는 마지막 날」과 이 날을 옆에 보이는 쓰기 창(설계 §5 · D4)이 쓴다.
 * slots 의 weekday 는 1 = 월요일 … 7 = 일요일(요청서 T11).
 */
export function lastClassDay(period, slots) {
  if (!period || !period.end_on || !Array.isArray(slots) || slots.length === 0) return null;
  const days = new Set(slots.map((s) => Number(s.weekday)));
  const a = toDayNumber(period.start_on);
  const e = toDayNumber(period.end_on);
  if (a == null || e == null) return null;
  for (let d = e - 1; d >= a && d >= e - 7; d -= 1) {
    if (days.has(((new Date(d * DAY_MS).getUTCDay() + 6) % 7) + 1)) return fromDayNumber(d);
  }
  return null;
}

// ── 학년(§4) — 번호표와 학교급. 학년 자체는 기간 행의 값 그대로라 여기서 셈하지 않는다 ──

/**
 * 학년 번호 → 학교급(ELEM·MIDDLE·HIGH). 13 졸업 · 14 비재학은 학교급이 없다(null) — 학교가 없는 것이 정상인 학년
 * (백엔드 2단계 전달 §2 · §4-D, 서버 grade_level 과 같음)
 */
export function schoolLevelOfGrade(grade) {
  if (grade == null) return null;
  if (grade <= 6) return 'ELEM';
  if (grade <= 9) return 'MIDDLE';
  if (grade <= 12) return 'HIGH';
  return null;
}

/** 학년 번호 → 「중2」 같은 이름. 13 은 고3 다음(졸업·N수), 14 는 학교에 다니지 않는 사람(자퇴·검정고시 등) */
export function gradeLabel(grade) {
  if (grade == null) return '';
  const g = Number(grade);
  if (g >= 1 && g <= 6) return `초${g}`;
  if (g >= 7 && g <= 9) return `중${g - 6}`;
  if (g >= 10 && g <= 12) return `고${g - 9}`;
  if (g === 13) return '졸업';
  if (g === 14) return '비재학';
  return '';
}

/** 학교 갱신 필요 — 학년이 학교의 학교급을 넘음. 학교가 비었거나 13·14 면 판정하지 않는다(§4) */
export function needsSchoolUpdate(schoolLevel, grade) {
  if (!schoolLevel || grade == null || grade >= 13) return false;
  return schoolLevelOfGrade(grade) !== schoolLevel;
}

// ── 표시 ──

/** '2026-11-01' → '11/1' */
export function formatMonthDay(date) {
  if (typeof date !== 'string' || date.length < 10) return '';
  return `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}`;
}

/**
 * 시간표 칸 → 「화·목 19:00~21:00」. 시각이 다른 칸은 묶음마다 쉼표로.
 * 칸: { weekday(ISO), start_time 'HH:MM', end_time 'HH:MM' }
 */
export function formatSchedule(slots) {
  if (!Array.isArray(slots) || slots.length === 0) return '';
  const groups = new Map();
  [...slots]
    .sort((a, b) => Number(a.weekday) - Number(b.weekday))
    .forEach((s) => {
      const key = `${s.start_time || ''}~${s.end_time || ''}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(weekdayLabel(Number(s.weekday)));
    });
  return [...groups.entries()].map(([time, days]) => `${days.join('·')} ${time}`).join(', ');
}
